import React from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";

// organizationBlock / webSiteBlock are intentionally NOT imported here: since
// Wave 2c the site-wide identity graph is baked into index.html at build time.
// faqBlock is intentionally not imported yet — see the note in buildGraph().
import {
  graphBlock,
  serviceBlock,
  breadcrumbBlock,
} from "../../../seo-schema/jsonld-blocks";
import { VERTICALS } from "../../../seo-schema/vertical-data";
import { buildLanguageAlternates } from "../../../seo-schema/hreflang-alternates";
import { SITEMAP_PATHS } from "../../../seo-schema/sitemap-data";

// Canonical production origin. JSON-LD @id / url nodes must be STABLE regardless
// of the environment serving the page (staging included) — the org entity is
// always https://flymya.com/#organization — so this is fixed, never derived from
// window.location.
const SITE_URL = "https://flymya.com";

// Normalize a pathname to { locale, page, hasLocalePrefix }:
//   /                   -> { locale: "en",    page: "",        hasLocalePrefix: false }
//   /en                 -> { locale: "en",    page: "",        hasLocalePrefix: true  }
//   /my-mm/flight       -> { locale: "my-mm", page: "flight",  hasLocalePrefix: true  }
//   /en/in-app/flight   -> { locale: "en",    page: "flight",  hasLocalePrefix: true  }
//   /flights            -> { locale: "en",    page: "flights", hasLocalePrefix: false }  (legacy)
//
// `hasLocalePrefix` matters: segment 0 is NOT always a locale. The legacy
// locale-less URLs (/, /flights) are separate 200-serving duplicates that today
// carry almost all of Google's traffic, so they must be distinguishable — see
// the canonical guard below.
function parsePath(pathname) {
  const segs = pathname.split("/").filter(Boolean);
  const hasLocalePrefix = segs[0] === "en" || segs[0] === "my-mm";
  const locale = segs[0] === "my-mm" ? "my-mm" : "en";
  const rest = (hasLocalePrefix ? segs.slice(1) : segs).filter(
    (s) => s !== "in-app" && s !== "in-web",
  );
  return { locale, page: rest.join("/"), hasLocalePrefix };
}

// Build the schema.org @graph for a logical page, or null if not yet covered.
//
// ROUTE-SPECIFIC BLOCKS ONLY (since Wave 2c). The site-wide identity graph
// (Organization + WebSite) is no longer emitted here — it is baked into
// index.html at build time from seo-schema/generated/site-graph.json, so that
// non-JS AI crawlers can read it. Emitting it here too would duplicate the same
// @ids on every page. See _context/AI-Friendly-SEO/WAVE_2C_SPEC.md §3.2.
//
// The flight FAQ is deliberately HELD until Operations verifies the airline list
// (adding faqBlock(flight.faq) here is the one-line Wave 2b change).
function buildGraph(page, locale) {
  if (page === "flight") {
    const flight = VERTICALS.find((v) => v.key === "flight");
    return graphBlock([
      serviceBlock({
        siteUrl: SITE_URL,
        locale,
        name: flight.name,
        description: flight.description,
        serviceType: flight.serviceType,
        path: flight.path,
      }),
      breadcrumbBlock([
        { name: "Home", item: `${SITE_URL}/${locale}` },
        { name: "Flights", item: `${SITE_URL}/${locale}/flight` },
      ]),
      // faqBlock(flight.faq) is DELIBERATELY STILL HELD — 2026-08-14.
      //
      // OPS confirmed the carrier list, so the DATA gate is now clear and the
      // five Q&As live in vertical-data.ts ready to go. The block is withheld
      // for two other reasons:
      //  1. Google requires FAQ structured data to match FAQ content VISIBLE on
      //     the page. The accordion is Wave 2b Part B (front-end) and hasn't
      //     shipped, so emitting it now would describe content that isn't there.
      //  2. The spec's whole justification for FAQPage is LLM grounding, not
      //     rich results (Google restricted FAQ rich results to gov/health in
      //     2023 — AI_SEO_PHASE1_SPEC.md §87). But everything SeoHead emits is
      //     client-injected, which AI crawlers cannot read. So shipping it here
      //     delivers none of its stated purpose.
      //
      // Release it when EITHER the visible accordion ships (Part B) OR it moves
      // to a server-rendered/static path where AI crawlers can actually see it.
      // See WAVE_2B_SCOPE.md §5.
    ]);
  }

  return null;
}

/**
 * Route-aware SEO head injector. Rendered once inside the Router; reads the
 * current path and emits, via react-helmet:
 *   - the ROUTE-SPECIFIC schema.org @graph (Service, BreadcrumbList, later FAQ)
 *     as <script type="application/ld+json">
 *   - a self-referencing <link rel="canonical">                   (Wave 2a)
 *   - <link rel="alternate" hreflang> for en / my-mm / x-default  (Wave 2a)
 *   - <meta property="og:url"> matching that canonical            (2026-08-26)
 *
 * NOT emitted here: the site-wide Organization + WebSite identity graph. Since
 * Wave 2c that is baked into index.html at build time, so it reaches non-JS AI
 * crawlers too. Keeping it out of here also avoids duplicate @ids.
 *
 * v4 caveat (PROJECT.md Decision Log 2026-07-13): everything THIS component
 * emits is client-injected — Googlebot (which renders JS) sees it, non-JS AI
 * crawlers do not. Per-page AI-crawler visibility rides the v5 migration
 * (Phase 3); Wave 2c covers only the company-identity layer.
 */
const SeoHead = () => {
  const { pathname } = useLocation();
  const { locale, page, hasLocalePrefix } = parsePath(pathname);

  const graph = buildGraph(page, locale);
  const path = page ? `/${page}` : ""; // "" = locale landing; matches SITEMAP_PATHS[0]

  // ---------------------------------------------------------------------------
  // CANONICAL GUARD (Wave 2a) — deliberately narrow. Emit canonical/hreflang ONLY
  // on paths that already carry an explicit /en or /my-mm prefix AND are in the
  // sitemap keep-list.
  //
  // WHY: every URL variant serves its own 200 (nothing redirects), and Google has
  // consolidated on the locale-LESS legacy URLs — "/" alone took 11,493 of ~11,900
  // clicks in the 28d to 2026-08-07, vs 9 for "/en". Emitting a canonical on "/"
  // would declare our top page a duplicate of one Google barely indexes. So we stay
  // silent there: this wave is additive and cannot move traffic. Consolidating the
  // legacy URLs is a separate, evidence-led decision and belongs in a server/edge
  // 301, not a client-side tag. See _context/AI-Friendly-SEO/WAVE_2A_SPEC.md §2/§7.
  // ---------------------------------------------------------------------------
  const emitHeadTags = hasLocalePrefix && SITEMAP_PATHS.includes(path);

  const canonicalHref = `${SITE_URL}/${locale}${path}`;

  const linkTags = emitHeadTags
    ? [
        { rel: "canonical", href: canonicalHref },
        ...Object.entries(buildLanguageAlternates(SITE_URL, path)).map(
          ([hreflang, href]) => ({ rel: "alternate", hreflang, href }),
        ),
      ]
    : [];

  // ---------------------------------------------------------------------------
  // og:url (2026-08-26) — rides the SAME guard as the canonical, deliberately.
  //
  // index.html hard-codes <meta property="og:url" content="https://flymya.com">,
  // so before this every route claimed the homepage as its Open Graph URL. On
  // /en/flight that directly CONTRADICTED the canonical we emit two lines above,
  // and every social share or AI link-preview of a vertical page was credited to
  // the homepage instead of the page being shared.
  //
  // og:url is meant to be the canonical URL of the object, so the correct value
  // is by definition the same string — hence one variable feeding both, which
  // also makes it impossible for them to drift apart later.
  //
  // Guard reuse matters as much as the fix: on "/" and the legacy locale-less
  // URLs we stay silent and index.html's static value stands, exactly as with
  // the canonical. Wave 2a's reasoning applies unchanged — "/" carries almost
  // all of Google's traffic and this wave must remain unable to move it.
  //
  // ⚠️ 2026-09-01 — this needed a second half, found on production.
  // react-helmet v6 manages ONLY the tags it created. index.html's static
  // og:url was invisible to it, so instead of replacing that tag it APPENDED a
  // second one. /en/hotel shipped both values:
  //   ["https://flymya.com", "https://flymya.com/en/hotel"]
  // Google reads the first, so the fix silently did nothing.
  // The canonical never hit this, because Wave 1a had already deleted the
  // static canonical, leaving react-helmet's tag alone in the head.
  // Fix: index.html now marks that tag `data-react-helmet="true"`, which hands
  // ownership to react-helmet. Do NOT delete the static tag instead — the guard
  // emits nothing on "/", so "/" would then carry no og:url at all.
  // ⚠️ Any future og: or twitter: tag added here repeats this bug unless the
  // matching static tag in index.html carries the same marker.
  // ---------------------------------------------------------------------------
  const metaTags = emitHeadTags
    ? [{ property: "og:url", content: canonicalHref }]
    : [];

  if (!graph && linkTags.length === 0 && metaTags.length === 0) return null;

  // Escape "<" so a stray "</script>" in any data value can never break the tag.
  const json = graph ? JSON.stringify(graph).replace(/</g, "\\u003c") : null;

  // `link` goes through the array prop (react-helmet 6 is unreliable with
  // dynamically mapped element children); the JSON-LD stays a child, the form
  // already proven in production since Wave 1b.
  return (
    <Helmet link={linkTags} meta={metaTags}>
      {json ? <script type="application/ld+json">{json}</script> : null}
    </Helmet>
  );
};

export default SeoHead;
